import React, { useState, useEffect } from 'react';
import './Analytics.css';

function Analytics({ user }) {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [keywordData, setKeywordData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const [analyticsResponse, roiResponse, keywordResponse] = await Promise.all([
        fetch(`https://contentvertex.zealcart.com/api/analytics/dashboard?range=${dateRange}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`https://contentvertex.zealcart.com/api/analytics/roi?range=${dateRange}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`https://contentvertex.zealcart.com/api/analytics/keywords?range=${dateRange}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (analyticsResponse.ok) {
        const analyticsResult = await analyticsResponse.json();
        setAnalyticsData(analyticsResult.data);
      }

      if (roiResponse.ok) {
        const roiResult = await roiResponse.json();
        setRoiData(roiResult.data);
      }

      if (keywordResponse.ok) {
        const keywordResult = await keywordResponse.json();
        setKeywordData(keywordResult.data);
      }

    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>Analytics Dashboard</h2>
        <div className="date-range-selector">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 3 months</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      <div className="analytics-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          Performance
        </button>
        <button 
          className={`tab-button ${activeTab === 'roi' ? 'active' : ''}`}
          onClick={() => setActiveTab('roi')}
        >
          ROI Analysis
        </button>
        <button 
          className={`tab-button ${activeTab === 'keywords' ? 'active' : ''}`}
          onClick={() => setActiveTab('keywords')}
        >
          Keywords
        </button>
      </div>

      {activeTab === 'overview' && analyticsData && (
        <div className="analytics-tab-content">
          <div className="stats-grid">
            <div className="stat-card views">
              <div className="stat-icon">👁️</div>
              <div className="stat-content">
                <h3>{formatNumber(analyticsData.overview.total_views)}</h3>
                <p>Total Views</p>
              </div>
            </div>
            
            <div className="stat-card posts">
              <div className="stat-icon">📝</div>
              <div className="stat-content">
                <h3>{analyticsData.overview.total_posts}</h3>
                <p>Published Posts</p>
              </div>
            </div>
            
            <div className="stat-card avg-views">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{analyticsData.overview.avg_views_per_post}</h3>
                <p>Avg Views/Post</p>
              </div>
            </div>
            
            <div className="stat-card shares">
              <div className="stat-icon">🔄</div>
              <div className="stat-content">
                <h3>{formatNumber(analyticsData.overview.total_social_shares)}</h3>
                <p>Social Shares</p>
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-card">
              <h3>Daily Views Trend</h3>
              <div className="simple-chart">
                {analyticsData.daily_analytics.map((day, index) => (
                  <div key={index} className="chart-bar">
                    <div 
                      className="bar" 
                      style={{
                        height: `${Math.max((day.views / Math.max(...analyticsData.daily_analytics.map(d => d.views))) * 100, 5)}%`
                      }}
                    ></div>
                    <span className="bar-label">{formatDate(day.date)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="chart-card">
              <h3>Top Performing Posts</h3>
              <div className="top-posts-list">
                {analyticsData.top_posts.map((post, index) => (
                  <div key={index} className="top-post-item">
                    <div className="post-rank">#{index + 1}</div>
                    <div className="post-details">
                      <h4>{post.topic_queue?.topic_title || 'Untitled'}</h4>
                      <div className="post-stats">
                        <span>{formatNumber(post.total_views)} views</span>
                        <span>{post.total_shares} shares</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'roi' && roiData && (
        <div className="analytics-tab-content">
          <div className="roi-grid">
            <div className="roi-card">
              <h3>Cost Analysis</h3>
              <div className="roi-metrics">
                <div className="roi-metric">
                  <span className="metric-label">Subscription Cost</span>
                  <span className="metric-value">${roiData.roi_metrics.subscription_cost}</span>
                </div>
                <div className="roi-metric">
                  <span className="metric-label">Cost per Post</span>
                  <span className="metric-value">${roiData.roi_metrics.cost_per_post}</span>
                </div>
                <div className="roi-metric">
                  <span className="metric-label">Cost per View</span>
                  <span className="metric-value">${roiData.roi_metrics.cost_per_view}</span>
                </div>
              </div>
            </div>

            <div className="roi-card">
              <h3>Performance Value</h3>
              <div className="value-metrics">
                <div className="value-item">
                  <span className="value-number">{formatNumber(roiData.roi_metrics.total_views)}</span>
                  <span className="value-label">Total Views Generated</span>
                </div>
                <div className="value-item">
                  <span className="value-number">{roiData.roi_metrics.total_posts}</span>
                  <span className="value-label">Articles Published</span>
                </div>
              </div>
            </div>
          </div>

          <div className="topic-performance-card">
            <h3>Topic Performance Ranking</h3>
            <div className="performance-list">
              {roiData.topic_performance.slice(0, 10).map((topic, index) => (
                <div key={index} className="performance-item">
                  <div className="performance-rank">#{index + 1}</div>
                  <div className="performance-details">
                    <h4>{topic.topic_title}</h4>
                    <div className="performance-stats">
                      <span>{formatNumber(topic.total_views)} views</span>
                      <span>{topic.total_shares} shares</span>
                      <span className="roi-value">
                        ROI: {((topic.total_views * 0.01) / (roiData.roi_metrics.cost_per_post)).toFixed(1)}x
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'keywords' && keywordData && (
        <div className="analytics-tab-content">
          <div className="keywords-grid">
            <div className="keywords-card">
              <h3>Keyword Rankings</h3>
              <div className="keywords-list">
                {keywordData.keyword_trends.slice(0, 10).map((keyword, index) => (
                  <div key={index} className="keyword-item">
                    <div className="keyword-details">
                      <h4>{keyword.keyword}</h4>
                      <div className="keyword-stats">
                        <span className="position">Position: {keyword.current_position || 'N/A'}</span>
                        <span className="trend">
                          {keyword.trend_data.length > 1 ? 
                            (keyword.trend_data[keyword.trend_data.length - 1].search_position < keyword.trend_data[0].search_position ? '📈' : '📉')
                            : '➖'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;